import React, { createContext, useState, useEffect, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/firebase";
import AuthService from "../services/auth_service";

export const AuthContext = createContext({
    user: null,
    loading: true,
    isAuthenticated: false,
    login: async () => {},
    loginWithGoogle: async () => {},
    logout: async () => {},
    resetPassword: async () => {}
});

const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 horas

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                // Montamos o objeto de usuário básico. TenantContext cuidará dos perfis mais profundos.
                setUser({
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName,
                    photoURL: firebaseUser.photoURL
                });
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const login = async (email, password) => {
        return AuthService.login(email, password);
    };

    const loginWithGoogle = async () => {
        return AuthService.socialLogin("google");
    };

    const logout = async () => {
        return AuthService.logout();
    };

    // --- TIMEOUT DE INATIVIDADE (8 HORAS) ---
    const timeoutRef = useRef(null);

    useEffect(() => {
        const handleIdleLogout = () => {
            if (user) {
                logout();
            }
        };

        const resetTimer = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (user) {
                timeoutRef.current = setTimeout(handleIdleLogout, IDLE_TIMEOUT_MS);
            }
        };

        // Adicionamos listeners apenas se houver usuário logado
        if (user) {
            const events = ['mousemove', 'keydown', 'wheel', 'mousedown', 'touchstart', 'touchmove'];
            
            resetTimer();

            events.forEach((event) => {
                window.addEventListener(event, resetTimer);
            });

            return () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                events.forEach((event) => {
                    window.removeEventListener(event, resetTimer);
                });
            };
        }
    }, [user]);

    const resetPassword = async (email) => {
        return AuthService.forgetPassword(email);
    };

    const verifyCodeAndChangePassword = async (email, code, newPassword) => {
        return AuthService.verifyCodeAndChangePassword(email, code, newPassword);
    };

    const value = {
        user,
        loading,
        isAuthenticated: !!user,
        login,
        loginWithGoogle,
        logout,
        resetPassword,
        verifyCodeAndChangePassword
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
